'use client';
import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQueryAll } from '../apolloClient';
import { useScreenSize } from "../hooks/ScreenSizeContext";

import Card from '@mui/material/Card';
import Grid from "@mui/material/Grid";
import TextField from '@mui/material/TextField';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import MDBox from './MDBox/MDBoxRoot';
import MDButton from './MDButton/MDButtonRoot';
import StyledBox from './StyleBox';
import setThemeObj from './../hooks/useThemeMUI';
import { SchoolTime } from '../utils/SchoolTime';
import * as types from '../gql/graphql';

const getPurchasesQuery = gql(`
  query GetPurchasesReport($after: Cursor, $from: Datetime, $to: Datetime) {
    fs_purchasesCollection(
      filter: {and: [{Date: {gte: $from}}, {Date: {lte: $to}}]}
      orderBy: {Date: DescNullsFirst}
      after: $after
    ) {
      edges {
        node {
          fs_students {
            id
            FirstName
            LastName
            Email
          }
          id
          fs_packages { code name }
          fs_classes { code name }
          Date
          Source
          PaymentValue
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const tableHeaders = [
  'User ID', 'First Name', 'Last Name', 'E-Mail', 'Price Paid', 'Date', 'Code', 'Name', 'Source'
];

const rowKeys = [
  'UserID', 'FirstName', 'LastName', 'Email', 'PaymentValue', 'Date', 'Code', 'Name', 'Source'
];

const formatPurchaseRow = (purchase: types.Fs_Purchases) => {
  const sourceMap: Record<number, string> = {
    1: "Online",
    2: "Admin",
    3: "Migrated"
  };

  const student = purchase.fs_students;

  return {
    UserID: student?.id || '',
    FirstName: student?.FirstName || '',
    LastName: student?.LastName || '',
    Email: student?.Email || '',
    Date: purchase.Date ? SchoolTime.fromISO(purchase.Date).toShortDateString() : '',
    Code: purchase.fs_packages?.code || purchase.fs_classes?.code || '',
    Name: purchase.fs_packages?.name || purchase.fs_classes?.name || '',
    Source: sourceMap[Number(purchase.Source)] || '',
    PaymentValue: purchase.PaymentValue || 'N/A',
  };
};

const PurchasesReport = () => {
  const today = SchoolTime.now();
  const oneMonthAgo = today.addMonths(-1);

  const [fromInput, setFromInput] = useState(oneMonthAgo.toDateTime().toISODate());
  const [toInput, setToInput] = useState(today.toDateTime().toISODate());
  const [dateFilter, setDateFilter] = useState({ from: oneMonthAgo, to: today });

  const { loading, error, data } = useQueryAll<types.Fs_PurchasesEdge>({
    query: getPurchasesQuery,
    variables: { from: dateFilter.from.toISOLocal(), to: dateFilter.to.toISOLocal() },
  });

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInput(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToInput(e.target.value);
  };

  const handleRunReport = () => {
    const fromDate = SchoolTime.fromISODate(fromInput);
    const toDate = SchoolTime.fromISODate(toInput);
    setDateFilter({ from: fromDate, to: toDate });
  };

  const downloadCSV = (rows) => {
    const headers = tableHeaders.join(",");
    const csvRows = rows.map(row =>
      rowKeys.map(key => {
        const cell = row[key];
        // Escape quotes
        const safe = typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell;
        return safe;
      }).join(",")
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `student_report_${fromInput}_to_${toInput}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <MDBox p={2}>Loading...</MDBox>;
  if (error) return <MDBox p={2}>Error loading purchases</MDBox>;

  const purchases = data.map(edge => edge.node);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Card>
          <MDBox component="main" px={4} py={4}>
            <StyledBox theme={setThemeObj} sx={{ marginBottom: 2 }}>
              <Grid container alignItems="flex-end" justifyContent="space-between">
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From:"
                    type="date"
                    value={fromInput}
                    onChange={handleFromDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <TextField
                    label="To:"
                    type="date"
                    value={toInput}
                    onChange={handleToDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleRunReport}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Run Report
                  </MDButton>
                </Grid>

                <Grid item>
                  <MDButton
                    variant="contained"
                    color="secondary"
                    onClick={() => downloadCSV(purchases.map(purchase => formatPurchaseRow(purchase)))}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Download
                  </MDButton>
                </Grid>
              </Grid>
            </StyledBox>

            <MDBox sx={{ maxHeight: '800px', overflowY: 'auto' }}>
              <MDBox component="table" sx={{ borderSpacing: "0px", width: "100%" }}>
                <MDBox
                  component="thead"
                  ownerState={{ variant: "contained", bgColor: "light" }}
                  sx={{
                    '& td': { fontSize: '12px' },
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                  }}
                  borderRadius={2}
                >
                  <MDBox component="tr">
                    {tableHeaders.map(header => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </MDBox>
                </MDBox>

                <TableBody sx={{ '& td': { fontSize: '12px' } }}>
                  {purchases.map(purchase => {
                    const row = formatPurchaseRow(purchase);
                    return (
                      <TableRow key={purchase.id}>
                        {rowKeys.map(key => (
                          <TableCell key={key}>{row[key]}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

export default PurchasesReport;
